<template>
  <div
    class="w-full min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-b from-gray-50 to-white font-sans py-8"
  >
    <!-- grid background pattern -->
    <div
      class="absolute inset-0 opacity-40 pointer-events-none"
      style="
        background-image:
          linear-gradient(#e5e7eb 1px, transparent 1px),
          linear-gradient(90deg, #e5e7eb 1px, transparent 1px);
        background-size: 40px 40px;
      "
    ></div>

    <!-- animated glow -->
    <div
      class="absolute top-0 right-0 w-96 h-96 bg-indigo-400/20 rounded-full blur-[100px] pointer-events-none animate-drift"
    ></div>
    <div
      class="absolute bottom-0 left-0 w-80 h-80 bg-emerald-400/15 rounded-full blur-[100px] pointer-events-none animate-drift-reverse"
    ></div>

    <!-- main content -->
    <div
      class="w-full max-w-[420px] px-6 relative z-10 max-h-[95vh] overflow-y-auto"
    >
      <!-- back button -->
      <div class="mb-6">
        <router-link
          to="/welcome"
          class="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 12H5m0 0l7-7 7 7" />
          </svg>
          <span class="text-sm font-medium">Retour</span>
        </router-link>
      </div>

      <!-- header -->
      <div class="mb-6 animate-fade-in">
        <div
          class="w-16 h-16 bg-gray-900 rounded-[16px] flex items-center justify-center shadow-lg mb-4"
        >
          <ImgComponent src="/logo.png" alt="" class="w-full h-full" />
        </div>
        <h1
          class="font-outfit font-bold text-3xl text-gray-900 tracking-tight mb-2"
        >
          Créer un compte
        </h1>
        <p class="text-gray-500">Rejoins 1xPay en quelques secondes</p>
      </div>

      <!-- form -->
      <form
        class="space-y-4 animate-slide-up"
        v-bus:EVENTS.USER.REGISTER.submit.prevent
        v-redirect-on="{
          event: $constants.EVENTS.USER.REGISTER_SUCCESS,
          to: '/'
        }"
      >
        <!-- Nom et Prénom -->
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-2">
            <label class="text-sm font-semibold text-gray-700">Nom</label>
            <input
              name="lastName"
              type="text"
              required
              class="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              placeholder="Votre nom"
            />
          </div>
          <div class="space-y-2">
            <label class="text-sm font-semibold text-gray-700">Prénom</label>
            <input
              name="firstName"
              type="text"
              required
              class="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              placeholder="Votre prénom"
            />
          </div>
        </div>

        <!-- Téléphone -->
        <div class="space-y-2">
          <label class="text-sm font-semibold text-gray-700">Téléphone</label>
          <div class="flex gap-2">
            <Listbox
              v-model="selectedCountry"
              as="div"
              class="relative flex-1 max-w-[110px]"
            >
              <ListboxButton
                ref="countryButton"
                class="w-full flex items-center gap-2 px-3 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              >
                <img
                  v-if="selectedCountry"
                  :src="selectedCountry.flag"
                  class="w-5 h-4 object-cover rounded-sm"
                />
                <span class="flex-1 text-left text-sm font-medium">{{
                  selectedCountry?.code || 'Code'
                }}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="text-gray-400"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </ListboxButton>
              <ListboxOptions
                class="absolute z-50 mt-1 max-h-60 w-auto min-w-full overflow-auto rounded-xl bg-white py-1 shadow-xl border border-gray-200 focus:outline-none text-sm"
              >
                <ListboxOption
                  v-for="country in countries"
                  :key="country.code"
                  :value="country"
                  class="relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                >
                  <img
                    :src="country.flag"
                    class="w-5 h-4 object-cover rounded-sm"
                  />
                  <span class="font-medium text-gray-900"
                    >+{{ country.code }}</span
                  >
                  <span class="text-gray-500 ml-1">{{ country.name }}</span>
                </ListboxOption>
              </ListboxOptions>
            </Listbox>
            <input
              name="phoneNumber"
              type="tel"
              required
              class="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              :placeholder="selectedCountry ? 'Ex: ' + selectedCountry.example : 'Choisir le pays'"
              @click="!selectedCountry && $refs.countryButton?.$el.click()"
              :readonly="!selectedCountry"
            />
            <input
              name="phonePrefix"
              type="hidden"
              :value="selectedCountry?.code || ''"
            />
          </div>
        </div>

        <!-- Email -->
        <div class="space-y-2">
          <label class="text-sm font-semibold text-gray-700">Email</label>
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-2"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                stroke-width="2"
              >
                <path
                  d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <input
              name="email"
              type="email"
              required
              class="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              placeholder="email@exemple.com"
            />
          </div>
        </div>

        <!-- Mot de passe -->
        <div class="space-y-2">
          <label class="text-sm font-semibold text-gray-700"
            >Mot de passe</label
          >
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-2"
            >
              <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <input
              name="password"
              type="password"
              required
              class="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              placeholder="********"
            />
          </div>
        </div>

        <!-- Code d'affiliation (optionnel) -->
        <!-- <div class="space-y-2">
          <label class="text-sm font-semibold text-gray-700">Code d'affiliation <span class="font-normal text-gray-400">(optionnel)</span></label>
          <input
            name="affiliationCode"
            type="text"
            class="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            placeholder="Code parrain"
          />
        </div> -->

        <!-- Numéro WhatsApp (optionnel) -->
        <div class="space-y-2">
          <label class="text-sm font-semibold text-gray-700"
            >Numéro WhatsApp
            <span class="font-normal text-gray-400">(optionnel)</span></label
          >
          <input
            name="whatsappNumber"
            type="tel"
            class="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            placeholder="Votre numéro WhatsApp"
          />
        </div>

        <!-- Cases à cocher -->
        <div class="space-y-3 pt-2">
          <label class="flex items-start gap-3 cursor-pointer">
            <input
              name="acceptMoreThan18"
              type="checkbox"
              required
              class="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span class="text-sm text-gray-600">J'ai plus de 18 ans</span>
          </label>

          <label class="flex items-start gap-3 cursor-pointer">
            <input
              name="termOfUse"
              type="checkbox"
              required
              class="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span class="text-sm text-gray-600">
              J'accepte les
              <a
                href="https://web.1xpay.co/cgu"
                target="_blank"
                class="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                conditions d'utilisation
              </a>
            </span>
          </label>
        </div>

        <!-- Bouton s'inscrire -->
        <button
          type="submit"
          class="w-full py-4 px-6 bg-primary text-white rounded-[14px] font-semibold text-base shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
          :disabled="$stores?.user?.state?.loading"
        >
          <span v-if="!$stores?.user?.state?.loading">Créer mon compte</span>
          <span v-else class="flex items-center gap-2">
            <svg
              class="animate-spin"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="3"
                class="opacity-25"
              />
              <path
                fill="currentColor"
                class="opacity-75"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Inscription...
          </span>
          <svg
            v-if="!$stores?.user?.state?.loading"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M5 12h14m-4-4l4 4-4 4" />
          </svg>
        </button>
      </form>

      <!-- Déjà un compte -->
      <div class="mt-6 text-center animate-fade-in">
        <p class="text-gray-500 text-sm">
          Déjà un compte ?
          <router-link
            to="/login"
            class="font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
          >
            Se connecter
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue';
import { COUNTRIES as countries } from '@features/shared/countries';

const selectedCountry = ref(null);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

.font-outfit {
  font-family: 'Outfit', sans-serif;
}

@keyframes drift {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(30px, 20px);
  }
}

@keyframes drift-reverse {
  0%,
  100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(-30px, 20px);
  }
}

.animate-drift {
  animation: drift 15s ease-in-out infinite;
}

.animate-drift-reverse {
  animation: drift-reverse 18s ease-in-out infinite;
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out forwards;
}

.animate-slide-up {
  animation: slide-up 0.6s ease-out 0.15s forwards;
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
